// src/components/localStorageUtils.js

const API_BASE_URL = "https://dashboard.plazor.xyz/api"; 

// -----------------------------
//  Utility: Auth Headers
// -----------------------------
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken"); 
  if (!token) throw new Error("User is not authenticated");
  return { Authorization: `Basic ${token}` };
};

// -----------------------------
//  TASKS
// -----------------------------
export const getTasks = async () => {
  const response = await fetch(`${API_BASE_URL}/tasks`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch tasks from server.");
  }
  const data = await response.json();
  return data.tasks || [];
};

let tasksSaveLock = false;
export const saveTasks = async (tasks) => {
  if (tasksSaveLock) return;
  tasksSaveLock = true;
  try {
    const response = await fetch(`${API_BASE_URL}/tasks`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ tasks }),
    });
    if (!response.ok) {
      throw new Error("Failed to save tasks to server.");
    }
  } finally {
    setTimeout(() => (tasksSaveLock = false), 1000);
  }
};

// -----------------------------
//  POINTS
// -----------------------------
export const getPoints = async () => {
  const response = await fetch(`${API_BASE_URL}/points`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch points from server.");
  }
  const data = await response.json();
  return data.points || 0;
};

export const savePoints = async (points) => {
  const response = await fetch(`${API_BASE_URL}/points`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ points }),
  });
  if (!response.ok) {
    throw new Error("Failed to save points to server.");
  }
};

// -----------------------------
//  REWARDS (including Mai NSFW Features)
// -----------------------------
export const getStoredRewards = async () => {
  const response = await fetch(`${API_BASE_URL}/rewards`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch rewards from server.");
  }
  const data = await response.json();
  return data.rewards || [];
};

export const saveRewardsToStorage = async (rewards) => {
  const response = await fetch(`${API_BASE_URL}/rewards`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ rewards }),
  });
  if (!response.ok) {
    throw new Error("Failed to save rewards to server.");
  }
};

export const resetRewardsInStorage = async () => {
  await saveRewardsToStorage([]);
};

// -----------------------------
//  LUCK
// -----------------------------
export const getLuckFromServer = async () => {
  const response = await fetch(`${API_BASE_URL}/luck`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch luck from server.");
  }
  const data = await response.json();
  return data.luck || 0;
};

export const saveLuckToServer = async (luck) => {
  const response = await fetch(`${API_BASE_URL}/luck`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ luck }),
  });
  if (!response.ok) {
    throw new Error("Failed to save luck to server.");
  }
};

// -----------------------------
//  KARA WORKOUTS
// -----------------------------
export const getKaraWorkouts = async () => {
  const response = await fetch(`${API_BASE_URL}/karaWorkouts`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Kara Workouts from server.");
  }
  return response.json(); // { calendar: [...], dayStatsHistory: [...] }
};

export const saveKaraWorkouts = async (calendar, dayStatsHistory) => {
  const response = await fetch(`${API_BASE_URL}/karaWorkouts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ calendar, dayStatsHistory }),
  });
  if (!response.ok) {
    throw new Error("Failed to save Kara Workouts to server.");
  }
};

// -----------------------------
//  VIOLET DREAMS
// -----------------------------
export const getVioletDreams = async () => {
  const response = await fetch(`${API_BASE_URL}/violetDreams`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch Violet Dreams from server.");
  }
  return response.json(); // { entries: [...] }
};

export const saveVioletDreams = async (entries) => {
  const response = await fetch(`${API_BASE_URL}/violetDreams`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ entries }),
  });
  if (!response.ok) {
    throw new Error("Failed to save Violet Dreams to server.");
  }
};

// -----------------------------
//  WEEKLY OUTCOME (including Blessed Mode)
// -----------------------------
export const getWeeklyOutcome = async () => {
  const response = await fetch(`${API_BASE_URL}/weeklyOutcome`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch weekly outcome from server.");
  }
  const data = await response.json();
  return data.weeklyOutcome || { mode: "neutral", streak: 0 };
};

export const saveWeeklyOutcome = async (weeklyOutcome) => {
  const response = await fetch(`${API_BASE_URL}/weeklyOutcome`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ weeklyOutcome }),
  });
  if (!response.ok) {
    throw new Error("Failed to save weekly outcome to server.");
  }
};

// -----------------------------
//  IKAROS WISH MODULE
// -----------------------------
// Expected format: { date: "YYYY-MM-DD", wish: string, response: string, granted: boolean | null }
export const getIkarosWish = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/ikarosWish`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch Ikaros wish from server.");
    }

    const data = await response.json();

    // Validate structure
    if (
      typeof data !== "object" ||
      !data.date ||
      typeof data.wish !== "string" ||
      typeof data.response !== "string" ||
      (data.granted !== null && typeof data.granted !== "boolean")
    ) {
      throw new Error("Invalid Ikaros wish data format.");
    }

    return data;
  } catch (error) {
    console.error("Error in getIkarosWish:", error);
    throw error;
  }
};

export const saveIkarosWish = async (wishData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/ikarosWish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify(wishData),
    });

    if (!response.ok) {
      throw new Error("Failed to save Ikaros wish to server.");
    }
  } catch (error) {
    console.error("Error in saveIkarosWish:", error);
    throw error;
  }
};


// -----------------------------
//  LONG-TERM GOALS
// -----------------------------
export const getLongTermGoals = async () => {
  const response = await fetch(`${API_BASE_URL}/longTermGoals`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch long-term goals from server.");
  }
  const data = await response.json();
  return data.longTermGoals || "";
};

export const updateLongTermGoals = async (longTermGoals) => {
  const response = await fetch(`${API_BASE_URL}/longTermGoals`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ longTermGoals }),
  });
  if (!response.ok) {
    throw new Error("Failed to update long-term goals to server.");
  }
};

export const deleteLongTermGoals = async () => {
  const response = await fetch(`${API_BASE_URL}/longTermGoals`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to delete long-term goals from server.");
  }
};

// -----------------------------
//  TIMESHEET ENTRIES (Year‑Round Tracking)
// -----------------------------
export const getTimesheetEntries = async () => {
  const response = await fetch(`${API_BASE_URL}/timesheetEntries`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error("Failed to fetch timesheet entries from server.");
  }
  const data = await response.json();
  // Expecting a structure keyed by week IDs (YYYY-MM-DD)
  return data.entries || {};
};

export const saveTimesheetEntries = async (entries) => {
  const response = await fetch(`${API_BASE_URL}/timesheetEntries`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
    },
    // Send the complete year‑round entries object keyed by week IDs.
    body: JSON.stringify({ entries }),
  });
  if (!response.ok) {
    throw new Error("Failed to save timesheet entries to server.");
  }
};
